/**
 * Application Launch Service — Version 2.0
 *
 * Every application launch in the platform routes through this service so
 * that adding a new launch backend (e.g. the future Coolman Operations
 * Launcher for desktop apps) is a change in ONE file — Dashboard components
 * do not decide how to launch anything.
 *
 * Contract:
 *   launchApplication(app, { navigate, onDialog })
 *
 * `app` is the Application Registry document. The service inspects
 * `launch_method` and either performs the launch directly or hands the
 * caller a payload to display an informational dialog. Failing silently is
 * NOT allowed — every code path either performs an action or emits a dialog
 * payload / error toast.
 *
 * Handlers (registered below):
 *   - internal_route   : react-router navigate to `launch_target`
 *   - external_url     : open `launch_target` in a new tab
 *   - desktop_launcher : emit info dialog — desktop launcher not yet built
 *   - none             : emit info dialog — display-only registration
 *
 * Extending: add a new key to `LAUNCH_HANDLERS`; return either
 *   { ok: true }                   — launch succeeded, nothing more to do
 *   { ok: false, dialog: {...} }   — caller should render the dialog
 *   { ok: false, error: "..." }    — caller should toast the error
 */
import { toast } from "sonner";

const DESKTOP_LAUNCHER_DIALOG = (app) => ({
  kind: "info",
  title: "Desktop application launch is not yet available",
  app,
  body: (
    "This application is registered in the Coolman Operations Platform, " +
    "but launching desktop applications from the browser will be supported " +
    "through the upcoming Coolman Operations Launcher. Until then, please " +
    "continue to open this application the way you do today."
  ),
});

const DISPLAY_ONLY_DIALOG = (app) => ({
  kind: "info",
  title: "This application is registered for reference only",
  app,
  body: (
    "No launch method has been configured for this application yet. " +
    "Ask an administrator to enable a launch method in the " +
    "Application Registry when it becomes ready."
  ),
});

function assertTarget(app) {
  if (!app.launch_target) {
    return {
      ok: false,
      error: `${app.name} has no launch target configured.`,
    };
  }
  return null;
}

const LAUNCH_HANDLERS = {
  internal_route: (app, { navigate }) => {
    const bad = assertTarget(app);
    if (bad) return bad;
    if (!navigate) {
      // Fallback if the caller didn't inject react-router's navigator.
      window.location.assign(app.launch_target);
      return { ok: true };
    }
    navigate(app.launch_target);
    return { ok: true };
  },

  external_url: (app) => {
    const bad = assertTarget(app);
    if (bad) return bad;
    const w = window.open(app.launch_target, "_blank", "noopener,noreferrer");
    if (!w) {
      return {
        ok: false,
        error: "Your browser blocked the popup. Allow popups for this site and try again.",
      };
    }
    return { ok: true };
  },

  desktop_launcher: (app) => ({
    ok: false,
    dialog: DESKTOP_LAUNCHER_DIALOG(app),
  }),

  none: (app) => ({
    ok: false,
    dialog: DISPLAY_ONLY_DIALOG(app),
  }),
};

export function launchApplication(app, opts = {}) {
  const handler = LAUNCH_HANDLERS[app.launch_method];
  if (!handler) {
    const err = `Unknown launch method '${app.launch_method}' for ${app.name}.`;
    if (opts.onError) opts.onError(err);
    else toast.error(err);
    return { ok: false, error: err };
  }
  const result = handler(app, opts);
  if (!result.ok) {
    if (result.dialog && opts.onDialog) {
      opts.onDialog(result.dialog);
    } else if (result.error) {
      if (opts.onError) opts.onError(result.error);
      else toast.error(result.error);
    }
  }
  return result;
}
